import React, { useEffect, useState, useContext, useRef } from "react";
import Page from "components/ui/Page";
import Header from "components/ui/Header";
import { Box, Flex, Grid, Link, Spinner } from "theme-ui";
import { BackArrowIcon, CheckMarkIcon } from "components/svg";
import { getProject, getProjectManualStatuses, updateProject } from "api/Projects";
import { trackActivity } from "api/ActivityTracker";
import { useNavigate, useParams } from "react-router-dom";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import moment from "moment-timezone";
import { Tab, Tabs } from "components/ui/Tabs";
import { themevals } from "theme/themevals";
import BasicButton from "components/forms/BasicButton";
import Subtext from "components/typography/Subtext";
import H5 from "components/typography/H5";
import TextInput from "components/forms/TextInput";
import SelectInput from "components/forms/SelectInput";
import SelectDate from "components/forms/SelectDate";
import Separator from "components/ui/Separator";
import GooglePlaces from "components/forms/GooglePlaces";
import { useToast } from "components/toast";
import UserContext from "context/User";
import { getClients } from "api/Clients";
import MultiText from "components/forms/MultiText";
import ToggleInput from "components/forms/ToggleInput";

const ProjectEdit = () => {
  const [project, setProject] = useState<any>(null);
  const [statusOptions, setStatusOptions] = useState<any>([]);
  const [companyClientOptions, setCompanyClientOptions] = useState<any>([]);
  const params = useParams();
  const { id } = params;
  const { user } = useContext<any>(UserContext);
  const toast = useToast();
  const navigate = useNavigate();
  const hasTrackedView = useRef(false);

  useEffect(() => {
    document.title = process.env.REACT_APP_NAME + " | Project";
    _init();
  }, []);

  const _init = async () => {
    const res = await getProject({ data: { id } });

    const statusRes = await getProjectManualStatuses();
    const options = Array.isArray(statusRes) ? statusRes.map((s: any) => ({ label: s.name, value: s.id })) : [];
    setStatusOptions(options);

    if (user.role !== null) {
      const clientRes = await getClients({});
      setCompanyClientOptions(clientRes.items.map((p: any) => ({ value: p.id, label: p.name })));
    }else{
      setCompanyClientOptions([{ value: res.primaryClient.id, label: `${res.primaryClient.name}` }]);
    }
  
    if (res) {
      setProject(res);
      
      // Track view after successful load
      if (!hasTrackedView.current) {
        hasTrackedView.current = true;
        void trackActivity({
          module: "projects",
          action: "view_project_edit",
          actionType: "view",
          userId: user?.id || null,
          companyId: user?.company?.id || null,
          entityType: "project",
          entityId: res.id,
          metadata: {},
          userAgent: typeof navigator !== "undefined" ? navigator.userAgent : null,
        });
      }
    }
  };

  const _validate = Yup.object().shape({
    projectName: Yup.string().required("Project name is required."),
  });

  const submitUpdate = async (values: any) => {
    const previousProject = project;
    
    const res = await updateProject({
      data: { id: project.id },
      update: {
        ...values,
        status: values.status ? { id: values.status } : undefined,
        location: values.location ? { id: values.location } : undefined,
        primaryClient: values.primaryClient ? { id: values.primaryClient } : undefined,
        serviceDate: values.serviceDate ? moment.unix(values.serviceDate).format("YYYY-MM-DD HH:mm:ss") : null,
        scheduleDate: values.scheduleDate ? moment.unix(values.scheduleDate).format("YYYY-MM-DD HH:mm:ss") : null,
        isRushOrder: values.isRushOrder,
        contactsCC: values.contactsCC ? values.contactsCC : [],
        siteContactName: values.siteContactName ? values.siteContactName : undefined,
        siteContactEmail: values.siteContactEmail ? values.siteContactEmail : undefined,
        siteContactPhone: values.siteContactPhone ? values.siteContactPhone : undefined,
        // Install-only: send supply fields when this is an install-only
        // project (serviceType gate), so a first-time method selection saves
        // even though supplyMethod started null. Backend propagates to unsent
        // POs (ProjectService.update).
        ...(values.serviceType === "Install Only"
          ? {
              serviceType: values.serviceType,
              supplyMethod: values.supplyMethod ?? null,
              supplySourceAddress: values.supplySourceAddress ?? null,
              onSiteLoadingRequired: !!values.onSiteLoadingRequired,
            }
          : {}),
      },
    });
    if (res.id) {
      if (previousProject) {
        const changedFields: string[] = [];

        if (res.projectName !== previousProject.projectName) {
          changedFields.push("projectName");
        }

        const prevPrimaryClientId = previousProject.primaryClient?.id || null;
        const newPrimaryClientId = res.primaryClient?.id || null;
        if (prevPrimaryClientId !== newPrimaryClientId) {
          changedFields.push("primaryClient");
        }

        const prevStatusId = previousProject.status?.id || null;
        const newStatusId = res.status?.id || null;
        if (prevStatusId !== newStatusId) {
          changedFields.push("status");

          const oldStatusName = previousProject.status?.name || null;
          const newStatusName = res.status?.name || null;

          void trackActivity({
            module: "projects",
            action: "change_project_status",
            actionType: "update",
            userId: user?.id || null,
            companyId: user?.company?.id || null,
            entityType: "project",
            entityId: res.id,
            metadata: {
              oldStatusName,
              newStatusName,
            },
            userAgent: typeof navigator !== "undefined" ? navigator.userAgent : null,
          });
        }

        const prevServiceDate = previousProject.serviceDate ? moment(previousProject.serviceDate) : null;
        const newServiceDate = res.serviceDate ? moment(res.serviceDate) : null;
        if (
          (prevServiceDate && newServiceDate && !prevServiceDate.isSame(newServiceDate)) ||
          (!!prevServiceDate !== !!newServiceDate)
        ) {
          changedFields.push("serviceDate");
        }

        const prevScheduleDate = previousProject.scheduleDate ? moment(previousProject.scheduleDate) : null;
        const newScheduleDate = res.scheduleDate ? moment(res.scheduleDate) : null;
        if (
          (prevScheduleDate && newScheduleDate && !prevScheduleDate.isSame(newScheduleDate)) ||
          (!!prevScheduleDate !== !!newScheduleDate)
        ) {
          changedFields.push("scheduleDate");
        }

        const prevClientPO = previousProject.clientPO || "";
        const newClientPO = res.clientPO || "";
        if (prevClientPO !== newClientPO) {
          changedFields.push("clientPO");
        }

        const prevContactsCC = Array.isArray(previousProject.contactsCC) ? previousProject.contactsCC : [];
        const newContactsCC = Array.isArray(res.contactsCC) ? res.contactsCC : [];
        if (JSON.stringify(prevContactsCC) !== JSON.stringify(newContactsCC)) {
          changedFields.push("contactsCC");
        }

        const prevSiteContactName = previousProject.siteContactName || "";
        const newSiteContactName = res.siteContactName || "";
        if (prevSiteContactName !== newSiteContactName) {
          changedFields.push("siteContactName");
        }

        const prevSiteContactEmail = previousProject.siteContactEmail || "";
        const newSiteContactEmail = res.siteContactEmail || "";
        if (prevSiteContactEmail !== newSiteContactEmail) {
          changedFields.push("siteContactEmail");
        }

        const prevSiteContactPhone = previousProject.siteContactPhone || "";
        const newSiteContactPhone = res.siteContactPhone || "";
        if (prevSiteContactPhone !== newSiteContactPhone) {
          changedFields.push("siteContactPhone");
        }

        const prevLocationId = previousProject.location?.id || null;
        const newLocationId = res.location?.id || null;
        if (prevLocationId !== newLocationId) {
          changedFields.push("location");
        }

        void trackActivity({
          module: "projects",
          action: "update_project",
          actionType: "update",
          userId: user?.id || null,
          companyId: user?.company?.id || null,
          entityType: "project",
          entityId: res.id,
          metadata: {
            changedFields,
          },
          userAgent: typeof navigator !== "undefined" ? navigator.userAgent : null,
        });
      }

      toast.add("Project updated.", "var(--theme-ui-colors-success)");
      setProject(res);
      navigate(`/projects/${res.id}`);
    }
  };

  return (
    <>
      <Header></Header>
      {!project && (
        <Box sx={{ textAlign: "center", paddingTop: "30px" }}>
          <Spinner size={25} strokeWidth={3} title="loading" />
        </Box>
      )}
      {project && (
        <Page
          title="Edit Project"
          subtitle="Edit the Project to update it's details."
          backAction={
            <Link variant="back" href={`/projects/${project?.id}`}>
              <BackArrowIcon />
              {`Back to Project`}
            </Link>
          }
        >
          <Formik
            initialValues={{
              projectName: project.projectName,
              serviceDate: project.serviceDate ? moment.utc(project.serviceDate).unix() : null,
              scheduleDate: project.scheduleDate ? moment.utc(project.scheduleDate).unix() : null,
              isRushOrder: project.isRushOrder || false,
              siteContactName: project.siteContactName ? project.siteContactName : "",
              siteContactEmail: project.siteContactEmail ? project.siteContactEmail : "",
              siteContactPhone: project.siteContactPhone ? project.siteContactPhone : "",
              primaryClient: project.primaryClient.id,
              contactsCC: project.contactsCC || [],
              location: project.location.id,
              status: project.status.id,
              clientPO: project.clientPO || "",
              // Install-only: project-level supply fields (source of truth; POs
              // inherit). Gated on serviceType (install-only), so the editor is
              // reachable even when supplyMethod hasn't been set yet.
              serviceType: project.serviceType || null,
              supplyMethod: project.supplyMethod || null,
              supplySourceAddress: project.supplySourceAddress || null,
              onSiteLoadingRequired: project.onSiteLoadingRequired || false,
              googlePlace: [
                project?.location.address?.line1,
                project?.location.address?.line2,
                [project?.location.address?.city, [project.location?.address?.state, project.location?.address?.postalCode].filter(Boolean).join(" "), project.location?.address?.country]
                  .filter(Boolean)
                  .join(", "),
              ]
                .filter(Boolean)
                .join(", "),
            }}
            onSubmit={submitUpdate}
            validationSchema={_validate}
          >
            {({ isSubmitting, errors, values, submitCount, setFieldValue, handleSubmit }) => (
              <Form noValidate autoComplete="off">
                <Tabs
                  vertical
                  stickyFooter
                  footer={
                    <Flex sx={{ gap: "10px", flexGrow: 1, justifyContent: "flex-end" }}>
                      <Link href={`/projects/${project.id}`} sx={{ ...themevals.buttons.secondaryOutline, textDecoration: "none", margin: "0 10px 0 0" }}>
                        Cancel
                      </Link>
                      <BasicButton type="submit" $submitting={isSubmitting} styles={{ margin: "0 10px 0 0" }} $iconHeight="14px" $iconWidth="14px" $loadingColor="#fff" keepText={true}>
                        {!isSubmitting && <CheckMarkIcon fill={"#fff"} />}
                        Save Project
                      </BasicButton>
                    </Flex>
                  }
                >
                  <Tab itemKey="general">
                    <Grid gap={6} columns={[1, "1fr 3fr"]} sx={{ paddingLeft: "30px", paddingRight: "30px" }}>
                      <Box>
                        <H5 fontFamily={"Poppins"} fontWeight={"600"} color={"#000"} marginTop={"0px"} marginBottom={"5px"}>
                          Basic Info
                        </H5>
                        <Subtext marginTop={0} marginBottom={0} fontSize={"12px"} fontWeight={"400"} fontFamily={"Poppins"}>
                          Edit basic information about the Quote.
                        </Subtext>
                      </Box>
                      <Grid gap={"20px"} columns={[1, "1fr 1fr"]}>
                        <Flex sx={{ flex: 1 }}>
                          <TextInput
                            name="projectName"
                            label="Project Name"
                            value={values.projectName}
                            onChange={(val: any) => setFieldValue("projectName", val)}
                            $errors={errors.projectName && submitCount > 0 ? errors.projectName : null}
                            $customStyles={{ flex: 1, marginBottom: "0px" }}
                          />
                        </Flex>
                        <Flex sx={{ flex: 1 }}>
                          {statusOptions.length > 0 && user.role !== null &&(
                            <SelectInput
                              name="status"
                              label="Status"
                              value={values.status}
                              options={statusOptions}
                              onChange={(val: any) => setFieldValue("status", val)}
                              $errors={errors.status && submitCount > 0 ? errors.status : null}
                              $customStyles={{ width: "100%" }}
                            />
                          )}
                        </Flex>
                        {user.role !== null && (
                        <Flex sx={{ flex: 1, marginTop: "10px" }}>
                          <SelectDate
                            name="serviceDate"
                            label="Estimated Service Date"
                            value={values.serviceDate}
                            onChange={(val: any) => {
                              setFieldValue("serviceDate", val);
                              // Auto-recalculate rush order when estimated service date changes (only if no scheduled date set)
                              if (project.createdAt && !values.scheduleDate) {
                                if (val) {
                                  const effectiveDateMs = val * 1000;
                                  const createdAtMs = new Date(project.createdAt).getTime();
                                  const diffDays = (effectiveDateMs - createdAtMs) / (1000 * 60 * 60 * 24);
                                  setFieldValue("isRushOrder", diffDays >= 0 && diffDays <= 5);
                                } else {
                                  setFieldValue("isRushOrder", false);
                                }
                              }
                            }}
                            $errors={errors.serviceDate && submitCount > 0 ? errors.serviceDate : null}
                            $customStyles={{ width: "50%", flexGrow: 1, marginBottom: "30px" }}
                            $removeMargin={false}
                          />
                        </Flex>
                        )}
                        {user.role !== null && (
                        <Flex sx={{ flex: 1, marginTop: "10px" }}>
                          <SelectDate
                            name="scheduleDate"
                            label="Scheduled Date"
                            value={values.scheduleDate}
                            onChange={(val: any) => {
                              setFieldValue("scheduleDate", val);
                              // Auto-recalculate rush order when scheduled date changes
                              if (project.createdAt) {
                                const effectiveDate = val || values.serviceDate;
                                if (effectiveDate) {
                                  const effectiveDateMs = effectiveDate * 1000;
                                  const createdAtMs = new Date(project.createdAt).getTime();
                                  const diffDays = (effectiveDateMs - createdAtMs) / (1000 * 60 * 60 * 24);
                                  setFieldValue("isRushOrder", diffDays >= 0 && diffDays <= 5);
                                } else {
                                  setFieldValue("isRushOrder", false);
                                }
                              }
                            }}
                            $errors={errors.scheduleDate && submitCount > 0 ? errors.scheduleDate : null}
                            $customStyles={{ width: "50%", flexGrow: 1, marginBottom: "30px" }}
                            $removeMargin={false}
                          />
                        </Flex>
                        )}
                        <Flex sx={{ flex: 1 }}>
                          {user.role !== null && (
                          <TextInput
                            name="clientPO"
                            label="Client PO"
                            value={values.clientPO}
                            onChange={(val: any) => setFieldValue("clientPO", val)}
                            $errors={errors.clientPO && submitCount > 0 ? errors.clientPO : null}
                            $customStyles={{ flex: 1, marginBottom: "0px" }}
                          />
                          )}
                        </Flex>
                        <Flex sx={{ flex: 1, alignItems: "flex-end" }}>
                          {user.role !== null && (
                            <ToggleInput
                              label="Rush Order"
                              description="Rush orders are not eligible for early payment discounts."
                              checked={values.isRushOrder}
                              onChange={(val: any) => setFieldValue("isRushOrder", val)}
                              $customStyles={{ width: "100%" }}
                            />
                          )}
                        </Flex>
                      </Grid>
                    </Grid>
                    <Separator />
                    <Grid gap={6} columns={[1, "1fr 3fr"]} sx={{ paddingLeft: "30px", paddingRight: "30px" }}>
                      <Box>
                        <H5 fontFamily={"Poppins"} fontWeight={"600"} color={"#000"} marginTop={"0px"} marginBottom={"5px"}>
                          Address
                        </H5>
                        <Subtext marginTop={0} marginBottom={0} fontSize={"12px"} fontWeight={"400"} fontFamily={"Poppins"}>
                          Edit the address associated with the Project.
                        </Subtext>
                      </Box>
                      <Box>
                        <GooglePlaces
                          label="Address"
                          value={values.googlePlace}
                          onChange={(val: any) => {
                            setFieldValue("address", val?.address);
                            setFieldValue("latitude", val?.latitude);
                            setFieldValue("longitude", val?.longitude);
                          }}
                          $errors={(errors?.address || errors?.latitude || errors?.longitude) && submitCount > 0 ? errors?.address ?? errors?.latitude ?? errors?.longitude : null}
                        />
                      </Box>
                    </Grid>
                    <Separator />
                    <Grid gap={6} columns={[1, "1fr 3fr"]} sx={{ paddingLeft: "30px", paddingRight: "30px" }}>
                      <Box>
                        <H5 fontFamily={"Poppins"} fontWeight={"600"} color={"#000"} marginTop={"0px"} marginBottom={"5px"}>
                          Contacts
                        </H5>
                        <Subtext marginTop={0} marginBottom={0} fontSize={"12px"} fontWeight={"400"} fontFamily={"Poppins"}>
                          Edit the contacts associated with the project.
                        </Subtext>
                      </Box>
                      <Grid gap={"20px"} columns={[1, "1fr 1fr"]}>
                        <Flex sx={{ flex: 1 }}>
                          <SelectInput
                            name="primaryClient"
                            label={user.role === null ? "Primary Contact" : " Primary Client"}
                            value={values.primaryClient}
                            options={companyClientOptions}
                            onChange={(val: any) => setFieldValue("primaryClient", val)}
                            $errors={errors.primaryClient && submitCount > 0 ? errors.primaryClient : null}
                            $customStyles={{ width: "100%" }}
                            disabled={user.role === null}
                          />
                        </Flex>
                        <Flex sx={{ flex: 1 }}>
                          {user.role !== null && (
                          <MultiText
                            name="contactsCC"
                            label="CC"
                            value={values.contactsCC}
                            onChange={(val: string[]) => setFieldValue("contactsCC", val)}
                            $errors={errors.contactsCC && submitCount > 0 ? errors.contactsCC : null}
                            $customStyles={{ width: "100%" }}
                          />
                          )}
                        </Flex>
                        {user.role !== null && (
                        <Flex sx={{ flex: 1 }}>
                          <TextInput
                            name="siteContactName"
                            label="Site Contact Name"
                            value={values.siteContactName}
                            onChange={(val: any) => setFieldValue("siteContactName", val)}
                            $errors={errors.siteContactName && submitCount > 0 ? errors.siteContactName : null}
                            $customStyles={{ flex: 1, marginBottom: "0px" }}
                          />
                        </Flex>
                        )}
                        {user.role !== null && (
                        <Flex sx={{ flex: 1 }}>
                          <TextInput
                            name="siteContactPhone"
                            label="Site Contact Phone"
                            value={values.siteContactPhone}
                            onChange={(val: any) => setFieldValue("siteContactPhone", val)}
                            $errors={errors.siteContactPhone && submitCount > 0 ? errors.siteContactPhone : null}
                            $customStyles={{ flex: 1, marginBottom: "0px" }}
                          />
                        </Flex>
                        )}
                        {user.role !== null && (
                        <Flex sx={{ flex: 1 }}>
                          <TextInput
                            name="siteContactEmail"
                            label="Site Contact Email"
                            value={values.siteContactEmail}
                            onChange={(val: any) => setFieldValue("siteContactEmail", val)}
                            $errors={errors.siteContactEmail && submitCount > 0 ? errors.siteContactEmail : null}
                            $customStyles={{ flex: 1, marginBottom: "0px" }}
                          />
                        </Flex>
                        )}
                      </Grid>
                    </Grid>
                    {values.serviceType === "Install Only" && (
                      <>
                        <Separator />
                        <Grid gap={6} columns={[1, "1fr 3fr"]} sx={{ paddingLeft: "30px", paddingRight: "30px" }}>
                          <Box>
                            <H5 fontFamily={"Poppins"} fontWeight={"600"} color={"#000"} marginTop={"0px"} marginBottom={"5px"}>
                              Mulch Supply
                            </H5>
                            <Subtext marginTop={0} marginBottom={0} fontSize={"12px"} fontWeight={"400"} fontFamily={"Poppins"}>
                              Install-only job. Material is supplied by a third party; set how it reaches the installer. This carries to the purchase order.
                            </Subtext>
                          </Box>
                          <Box>
                            <Flex sx={{ flexDirection: "column", gap: "8px", marginBottom: "16px" }}>
                              {[
                                { key: "pickup_from_fibar", label: "Pickup from supplier", sub: "Installer collects from the supplier's depot." },
                                { key: "delivery_to_site", label: "Supplier delivers to site", sub: "On-site loading may be required." },
                                { key: "delivery_to_yard", label: "Supplier delivers to yard", sub: "Supplier delivers to the installer's yard." },
                              ].map((opt) => {
                                const on = values.supplyMethod === opt.key;
                                return (
                                  <Box
                                    key={opt.key}
                                    onClick={() => {
                                      setFieldValue("supplyMethod", opt.key);
                                      if (opt.key !== "delivery_to_site") setFieldValue("onSiteLoadingRequired", false);
                                    }}
                                    sx={{
                                      cursor: "pointer",
                                      border: `1px solid ${on ? themevals.colors.black : themevals.colors.mdgrey}`,
                                      borderRadius: "6px",
                                      padding: "10px 12px",
                                      background: on ? themevals.colors.base_100 : "#fff",
                                    }}
                                  >
                                    <Flex sx={{ alignItems: "center", gap: "8px" }}>
                                      <Box
                                        sx={{
                                          width: "15px", height: "15px", borderRadius: "50%", flex: "0 0 auto",
                                          border: `1.5px solid ${on ? themevals.colors.black : themevals.colors.mdgrey}`,
                                          background: on ? "radial-gradient(" + themevals.colors.black + " 42%, #fff 46%)" : "#fff",
                                        }}
                                      />
                                      <Box sx={{ fontFamily: "Poppins", fontSize: "13px", fontWeight: on ? 600 : 400 }}>{opt.label}</Box>
                                    </Flex>
                                    <Box sx={{ fontFamily: "Poppins", fontSize: "11px", color: themevals.colors.dkgrey, marginTop: "4px", paddingLeft: "23px" }}>{opt.sub}</Box>
                                  </Box>
                                );
                              })}
                            </Flex>

                            {values.supplyMethod === "pickup_from_fibar" && (
                              <Flex sx={{ flex: 1, marginBottom: "16px", flexDirection: "column" }}>
                                <GooglePlaces
                                  name="supplyPickupLocation"
                                  label="Supplier pickup location *"
                                  value={values.supplySourceAddress ? [values.supplySourceAddress.line1, values.supplySourceAddress.city, values.supplySourceAddress.state, values.supplySourceAddress.postalCode].filter(Boolean).join(", ") : ""}
                                  onChange={(val: any) => {
                                    if (val && val.address) {
                                      setFieldValue("supplySourceAddress", {
                                        ...val.address,
                                        latitude: val.latitude,
                                        longitude: val.longitude,
                                      });
                                    } else if (val === null) {
                                      setFieldValue("supplySourceAddress", null);
                                    }
                                  }}
                                  $errors={submitCount > 0 && !values.supplySourceAddress?.line1 ? "A pickup location is required before the PO is sent." : null}
                                  $customStyles={{ flex: 1, marginBottom: "0px" }}
                                />
                              </Flex>
                            )}

                            {values.supplyMethod === "delivery_to_yard" && (
                              <Flex sx={{ flex: 1, marginBottom: "16px", flexDirection: "column" }}>
                                <GooglePlaces
                                  name="supplyYardLocation"
                                  label="Installer yard location"
                                  value={values.supplySourceAddress ? [values.supplySourceAddress.line1, values.supplySourceAddress.city, values.supplySourceAddress.state, values.supplySourceAddress.postalCode].filter(Boolean).join(", ") : ""}
                                  onChange={(val: any) => {
                                    if (val && val.address) {
                                      setFieldValue("supplySourceAddress", {
                                        ...val.address,
                                        latitude: val.latitude,
                                        longitude: val.longitude,
                                      });
                                    } else if (val === null) {
                                      setFieldValue("supplySourceAddress", null);
                                    }
                                  }}
                                  $customStyles={{ flex: 1, marginBottom: "0px" }}
                                />
                                <Box sx={{ fontFamily: "Poppins", fontSize: "11px", color: themevals.colors.dkgrey, marginTop: "6px" }}>
                                  Where the supplier delivers the material for the installer to collect. Defaults to the supplier depot; change it if the installer uses a different yard.
                                </Box>
                              </Flex>
                            )}

                            {values.supplyMethod === "delivery_to_site" && (
                              <ToggleInput
                                label="On-site loading required"
                                description="The installer will need loading equipment on site."
                                checked={values.onSiteLoadingRequired}
                                onChange={(val: any) => setFieldValue("onSiteLoadingRequired", val)}
                                $customStyles={{ width: "100%" }}
                              />
                            )}
                          </Box>
                        </Grid>
                      </>
                    )}
                  </Tab>
                </Tabs>
              </Form>
            )}
          </Formik>
        </Page>
      )}
    </>
  );
};

export default ProjectEdit;
